import React from "react"
import { useFetcher } from "react-router"
import { PlusIcon, TrashIcon, XCircleIcon, SaveIcon, LoaderCircleIcon, Pencil } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { PopoverClose } from "@radix-ui/react-popover"

import type { ActionResponse } from "@/utils/types"
import type { LoaderData } from "./brand-ui-config"

interface JSChild {
  src: string
}

interface JSFile {
  src: string
  children?: JSChild[]
}

interface QuestionPackConfig {
  renderInputTextOnly?: boolean
  css?: string[]
  js?: JSFile[]
}

export function QuestionPackTab({ config, data }: { config: QuestionPackConfig | undefined; data: LoaderData }) {
  const [cssFiles, setCssFiles] = React.useState<string[]>(config?.css ?? [])
  const [jsFiles, setJsFiles] = React.useState<JSFile[]>(config?.js ?? [])
  const [editingCss, setEditingCss] = React.useState<number | null>(null)
  const [editingJs, setEditingJs] = React.useState<{ parentIdx: number; childIdx?: number } | null>(null)
  const [showAddCssDialog, setShowAddCssDialog] = React.useState(false)
  const [showAddJsDialog, setShowAddJsDialog] = React.useState(false)
  const [showAddChildDialog, setShowAddChildDialog] = React.useState<number | null>(null)

  const fetcher = useFetcher<ActionResponse>()

  React.useEffect(() => {
    setCssFiles(config?.css ?? [])
    setJsFiles(config?.js ?? [])
  }, [config])

  const addCssFile = (file: string) => {
    setCssFiles((prev) => [...prev, file])
    setShowAddCssDialog(false)
  }

  const updateCssFile = (index: number, file: string) => {
    setCssFiles((prev) => prev.map((f, i) => (i === index ? file : f)))
    setEditingCss(null)
  }

  const removeCssFile = (index: number) => {
    setCssFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const addJsFile = (src: string) => {
    setJsFiles((prev) => [...prev, { src, children: [] }])
    setShowAddJsDialog(false)
  }

  const updateJsFile = (parentIdx: number, src: string, childIdx?: number) => {
    setJsFiles((prev) =>
      prev.map((jsFile, i) => {
        if (i !== parentIdx) return jsFile
        if (childIdx === undefined) {
          return { ...jsFile, src }
        } else {
          return {
            ...jsFile,
            children: jsFile.children?.map((child, ci) => (ci === childIdx ? { src } : child)),
          }
        }
      }),
    )
    setEditingJs(null)
  }

  const removeJsFile = (parentIdx: number, childIdx?: number) => {
    if (childIdx === undefined) {
      setJsFiles((prev) => prev.filter((_, i) => i !== parentIdx))
    } else {
      setJsFiles((prev) =>
        prev.map((jsFile, i) => {
          if (i !== parentIdx) return jsFile
          return {
            ...jsFile,
            children: jsFile.children?.filter((_, ci) => ci !== childIdx),
          }
        }),
      )
    }
  }

  const addChildFile = (parentIdx: number, src: string) => {
    setJsFiles((prev) =>
      prev.map((jsFile, i) => {
        if (i !== parentIdx) return jsFile
        return {
          ...jsFile,
          children: [...(jsFile.children ?? []), { src }],
        }
      }),
    )
    setShowAddChildDialog(null)
  }

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="mx-auto w-full space-y-6">
          <div>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold">CSS Files</h2>
              <Button onClick={() => setShowAddCssDialog(true)} variant="outline" size="sm">
                <PlusIcon className="mr-2 h-4 w-4" />
                Add CSS File
              </Button>
            </div>

            {cssFiles.length === 0 ? (
              <div className="rounded-lg border-2 border-dashed border-border py-8 text-center text-muted-foreground">
                <p className="text-sm">No CSS files configured</p>
              </div>
            ) : (
              <div className="space-y-2">
                {cssFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between rounded-lg border p-3">
                    <span className="font-mono text-sm">{file}</span>
                    <div className="flex gap-2">
                      <Button onClick={() => setEditingCss(idx)} variant="ghost" size="sm">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => removeCssFile(idx)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold">JavaScript Files</h2>
              <Button onClick={() => setShowAddJsDialog(true)} variant="outline" size="sm">
                <PlusIcon className="mr-2 h-4 w-4" />
                Add JS File
              </Button>
            </div>

            {jsFiles.length === 0 ? (
              <div className="rounded-lg border-2 border-dashed border-border py-8 text-center text-muted-foreground">
                <p className="text-sm">No JavaScript files configured</p>
              </div>
            ) : (
              <div className="space-y-3">
                {jsFiles.map((jsFile, parentIdx) => (
                  <div key={parentIdx} className="rounded-lg border p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm">{jsFile.src}</span>
                      <div className="flex gap-2">
                        <Button onClick={() => setEditingJs({ parentIdx })} variant="ghost" size="sm">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => setShowAddChildDialog(parentIdx)}
                          variant="ghost"
                          size="sm"
                          className="text-info hover:bg-info/10"
                        >
                          <PlusIcon className="mr-1 h-3 w-3" />
                          Add Child
                        </Button>
                        <Button
                          onClick={() => removeJsFile(parentIdx)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {jsFile.children != null && jsFile.children.length > 0 && (
                      <div className="mt-2 ml-8 space-y-1">
                        {jsFile.children.map((child, childIdx) => (
                          <div
                            key={childIdx}
                            className="flex items-center justify-between rounded border-l-2 border-input bg-muted py-2 pr-2 pl-4"
                          >
                            <span className="font-mono text-sm text-foreground">↳ {child.src}</span>
                            <div className="flex gap-2">
                              <Button onClick={() => setEditingJs({ parentIdx, childIdx })} variant="ghost" size="sm">
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                onClick={() => removeJsFile(parentIdx, childIdx)}
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:bg-red-50"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-shrink-0 border-t p-4">
        <div className="mx-auto flex w-full gap-3">
          <fetcher.Form method="post" className="flex-1">
            <input type="hidden" name="action" value="SAVE_SECTION" />
            <input type="hidden" name="organisation" value={data.loadedOrganisation} />
            <input type="hidden" name="brand" value={data.loadedBrand} />
            <input type="hidden" name="jsonPath" value="$.questionPack" />
            <input type="hidden" name="value" value={JSON.stringify({ css: cssFiles, js: jsFiles })} />
            <input type="hidden" name="concurrency" value={data.config?.concurrency ?? ""} />
            <Button type="submit" disabled={fetcher.state !== "idle"} className="w-full bg-info hover:bg-info/90">
              {fetcher.state !== "idle" && fetcher.formData?.get("action") === "SAVE_SECTION" && (
                <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
              )}
              <SaveIcon className="h-4 w-4" />
              Save Question Pack
            </Button>
          </fetcher.Form>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="flex-1 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                disabled={fetcher.state !== "idle"}
              >
                {fetcher.state !== "idle" && fetcher.formData?.get("action") === "REMOVE_SECTION" && (
                  <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
                )}
                <XCircleIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Remove Section</span>
                <span className="sm:hidden">Remove</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent side="top" className="w-80">
              <div className="space-y-3">
                <p className="text-sm font-medium">Remove Question Pack Configuration?</p>
                <p className="text-sm text-muted-foreground">
                  This will remove the entire question pack configuration from this level.
                </p>
                <p className="text-sm font-medium text-red-600">This action cannot be undone.</p>
                <div className="flex items-center justify-end gap-3">
                  <PopoverClose asChild>
                    <Button variant="outline" size="sm">
                      Cancel
                    </Button>
                  </PopoverClose>
                  <PopoverClose asChild>
                    <fetcher.Form method="post">
                      <input type="hidden" name="action" value="REMOVE_SECTION" />
                      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
                      <input type="hidden" name="brand" value={data.loadedBrand} />
                      <input type="hidden" name="jsonPath" value="$.questionPack" />
                      <input type="hidden" name="concurrency" value={data.config?.concurrency ?? ""} />
                      <Button type="submit" variant="destructive" size="sm" disabled={fetcher.state !== "idle"}>
                        {fetcher.state !== "idle" && <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />}
                        Remove
                      </Button>
                    </fetcher.Form>
                  </PopoverClose>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {(showAddCssDialog || editingCss !== null) && (
        <FileDialog
          open={true}
          onClose={() => {
            setShowAddCssDialog(false)
            setEditingCss(null)
          }}
          onSave={(file) => {
            if (editingCss !== null) {
              updateCssFile(editingCss, file)
            } else {
              addCssFile(file)
            }
          }}
          initialValue={editingCss !== null ? cssFiles[editingCss] : undefined}
          title={editingCss !== null ? "Edit CSS File" : "Add CSS File"}
          placeholder="e.g., css/custom/questionpacks.css"
        />
      )}

      {(showAddJsDialog || (editingJs !== null && editingJs.childIdx === undefined)) && (
        <FileDialog
          open={true}
          onClose={() => {
            setShowAddJsDialog(false)
            setEditingJs(null)
          }}
          onSave={(file) => {
            if (editingJs !== null) {
              updateJsFile(editingJs.parentIdx, file)
            } else {
              addJsFile(file)
            }
          }}
          initialValue={editingJs !== null ? jsFiles[editingJs.parentIdx]?.src : undefined}
          title={editingJs !== null ? "Edit JS File" : "Add JS File"}
          placeholder="e.g., js/vendor/jquery-3.6.0.min.js"
        />
      )}

      {(showAddChildDialog !== null || editingJs?.childIdx !== undefined) && (
        <FileDialog
          open={true}
          onClose={() => {
            setShowAddChildDialog(null)
            setEditingJs(null)
          }}
          onSave={(file) => {
            if (editingJs?.childIdx !== undefined) {
              updateJsFile(editingJs.parentIdx, file, editingJs.childIdx)
            } else if (showAddChildDialog !== null) {
              addChildFile(showAddChildDialog, file)
            }
          }}
          initialValue={
            editingJs?.childIdx !== undefined
              ? jsFiles[editingJs.parentIdx]?.children?.[editingJs.childIdx]?.src
              : undefined
          }
          title={editingJs?.childIdx !== undefined ? "Edit Child JS File" : "Add Child JS File"}
          placeholder="e.g., js/vendor/jquery.timepicker-1.11.9-custom.min.js"
        />
      )}
    </div>
  )
}

function FileDialog({
  open,
  onClose,
  onSave,
  initialValue,
  title,
  placeholder,
}: {
  open: boolean
  onClose: () => void
  onSave: (file: string) => void
  initialValue?: string
  title: string
  placeholder: string
}) {
  const [file, setFile] = React.useState(initialValue ?? "")

  React.useEffect(() => {
    if (open) {
      setFile(initialValue ?? "")
    }
  }, [open, initialValue])

  const handleSave = () => {
    if (file.trim().length === 0) return
    onSave(file.trim())
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium">File Path *</label>
            <Input
              value={file}
              onChange={(e) => setFile(e.target.value)}
              placeholder={placeholder}
              className="font-mono"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t pt-4">
          <DialogClose asChild>
            <Button variant="outline" type="button">
              Cancel
            </Button>
          </DialogClose>
          <Button onClick={handleSave} disabled={file.trim().length === 0} type="button">
            {initialValue != null ? "Save" : "Add"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
